import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './config';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';
import type { CrewContractColumn } from '../crew-wage-columns/useData';

export interface CrewWageScaleValue {
  id?: number;
  columnId: number;
  amount: number | string;
  column?: Pick<
    CrewContractColumn,
    'id' | 'code' | 'name' | 'columnType' | 'dataType' | 'includeInTotal' | 'sortOrder' | 'isActive'
  >;
}

export interface CrewWageScale {
  id: number;
  vesselId: number;
  rankId: number;
  nationalityId: number;
  currency?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  isActive: boolean;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  vessel?: { id: number; vesselName: string; vesselAbrv?: string | null };
  rank?: { id: number; code?: string | null; name: string; abrv?: string | null };
  nationality?: { id: number; code?: string | null; name: string };
  values?: CrewWageScaleValue[];
}

export interface CrewWageLookups {
  vessels: Array<{ id: number; vesselName: string }>;
  ranks: Array<{ id: number; code?: string | null; name: string; abrv?: string | null }>;
  nationalities: Array<{ id: number; code?: string | null; name: string }>;
  currencies: Array<{ id: number; code: string; name: string; symbol?: string | null }>;
  contractColumns: CrewContractColumn[];
}

export const useCrewWageScales = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<CrewWageScale> | CrewWageScale[]
  >(`${moduleConfig.module.api.list}${q}`, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse<CrewWageScale>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useCrewWageScale = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<CrewWageScale>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};

export const useCrewWageLookups = () => {
  const { data, error, isLoading } = useSWR<CrewWageLookups>('/crew/lookups', fetcher, {
    revalidateOnFocus: false,
  });
  return { lookups: data, isLoading, isError: error };
};
